import {createClient} from '@supabase/supabase-js';
import '../polyfills/url-polyfill'; // Import the URL polyfill

// Replace these with your actual Supabase URL and anon key
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Initialize with proper auth options for better session persistence
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: localStorage, // Explicitly use localStorage for session storage
  },
});

// Global subscription manager to prevent duplicate subscriptions
class SubscriptionManager {
  private static instance: SubscriptionManager;
  private activeSubscriptions: Map<
    string,
    {
      channel: any;
      tables: Set<string>;
      count: number;
      status: 'connecting' | 'connected' | 'error';
      retryCount: number;
    }
  > = new Map();
  private maxRetries = 3;

  private constructor() {}

  public static getInstance(): SubscriptionManager {
    if (!SubscriptionManager.instance) {
      SubscriptionManager.instance = new SubscriptionManager();
    }
    return SubscriptionManager.instance;
  }

  public async subscribe(
    channelName: string,
    table: string,
    event: 'INSERT' | 'UPDATE' | 'DELETE' | '*',
    filter: string,
    callback: (payload: any) => void,
    properties?: string[],
  ): Promise<{unsubscribe: () => void}> {
    // Create a unique identifier for this subscription
    const key = `${channelName}`;

    // Check if channel already exists and is connected
    if (this.activeSubscriptions.has(key)) {
      const subscription = this.activeSubscriptions.get(key)!;

      // Only reuse if the subscription is healthy
      if (subscription.status === 'connected') {
        subscription.count++;
        subscription.tables.add(table);

        // If channel exists, just add the listener
        subscription.channel.on(
          'postgres_changes',
          {
            event,
            schema: 'public',
            table,
            filter,
            properties,
          },
          callback,
        );

        return {
          unsubscribe: () => {
            this.unsubscribe(key, table);
          },
        };
      }
    }

    // Ensure we have a valid session before creating subscriptions
    const sessionStatus = await getSessionStatus();
    if (!sessionStatus.hasSession) {
      throw new Error('No valid session for subscription');
    }

    // Create new channel
    const channel = supabase.channel(channelName);

    // Add listener
    channel.on(
      'postgres_changes',
      {
        event,
        schema: 'public',
        table,
        filter,
        properties,
      },
      callback,
    );

    // Store the subscription with initial status
    this.activeSubscriptions.set(key, {
      channel,
      tables: new Set([table]),
      count: 1,
      status: 'connecting',
      retryCount: 0,
    });

    // Subscribe to the channel with status handling
    try {
      const subscriptionResult = channel.subscribe();

      // For web, we'll use a simpler approach - assume success if no error is thrown
      // and set up a timeout to check if the subscription is working
      setTimeout(() => {
        const currentSubscription = this.activeSubscriptions.get(key);
        if (
          currentSubscription &&
          currentSubscription.status === 'connecting'
        ) {
          // If still connecting after 5 seconds, assume it's connected
          // This is a simplified approach for web
          currentSubscription.status = 'connected';
        }
      }, 5000);
    } catch (error) {
      this.activeSubscriptions.delete(key);
      throw error;
    }

    return {
      unsubscribe: () => {
        this.unsubscribe(key, table);
      },
    };
  }

  private handleSubscriptionStatus(
    key: string,
    status: string,
    err?: any,
  ): void {
    const subscription = this.activeSubscriptions.get(key);
    if (!subscription) return;

    switch (status) {
      case 'SUBSCRIBED':
        subscription.status = 'connected';
        subscription.retryCount = 0;
        break;

      case 'CHANNEL_ERROR':
        subscription.status = 'error';
        this.handleSubscriptionError(key, 'CHANNEL_ERROR', err);
        break;

      case 'CLOSED':
        subscription.status = 'error';
        this.handleSubscriptionError(key, 'CLOSED', err);
        break;

      case 'TIMED_OUT':
        subscription.status = 'error';
        this.handleSubscriptionError(key, 'TIMED_OUT', err);
        break;

      default:
    }
  }

  private handleSubscriptionError(
    key: string,
    errorType: string,
    err?: any,
  ): void {
    const subscription = this.activeSubscriptions.get(key);
    if (!subscription) return;

    // Check if we should retry
    if (subscription.retryCount >= this.maxRetries) {
      return;
    }

    subscription.retryCount++;

    // Calculate delay with exponential backoff
    let delay = 2000; // Base delay
    if (errorType === 'CLOSED') {
      delay = 5000;
    } else if (errorType === 'TIMED_OUT') {
      delay = 1000;
    }
    delay = delay * Math.pow(2, subscription.retryCount - 1);

    setTimeout(async () => {
      try {
        // Check if subscription still exists (might have been cleaned up)
        const currentSubscription = this.activeSubscriptions.get(key);
        if (!currentSubscription || currentSubscription.count === 0) {
          return;
        }

        // Remove the failed channel
        supabase.removeChannel(currentSubscription.channel);

        // Create new channel with same configuration
        const newChannel = supabase.channel(key);

        // Re-add all listeners (this is simplified - in practice you'd need to store the original configs)
        // For now, we'll just mark it as connecting and let the next subscription attempt handle it
        currentSubscription.channel = newChannel;
        currentSubscription.status = 'connecting';

        // Subscribe with status handler
        const retryResult = newChannel.subscribe();

        // Set up timeout to assume success after 5 seconds
        setTimeout(() => {
          const retrySubscription = this.activeSubscriptions.get(key);
          if (retrySubscription && retrySubscription.status === 'connecting') {
            retrySubscription.status = 'connected';
          }
        }, 5000);
      } catch (retryError) {
    // Console statement removed
  }
    }, delay);
  }

  private unsubscribe(key: string, table: string): void {
    if (!this.activeSubscriptions.has(key)) return;

    const subscription = this.activeSubscriptions.get(key)!;
    subscription.count--;
    subscription.tables.delete(table);

    // If no more references, remove the channel
    if (subscription.count === 0 || subscription.tables.size === 0) {
      supabase.removeChannel(subscription.channel);
      this.activeSubscriptions.delete(key);
    }
  }

  // Debug methods
  public getActiveSubscriptionCount(): number {
    return this.activeSubscriptions.size;
  }

  public getSubscriptionStatus(key: string): string | undefined {
    return this.activeSubscriptions.get(key)?.status;
  }

  public getActiveSubscriptions(): string[] {
    return Array.from(this.activeSubscriptions.keys());
  }

  // Health check method
  public getSubscriptionHealth(): {
    total: number;
    connected: number;
    connecting: number;
    error: number;
  } {
    const health = {total: 0, connected: 0, connecting: 0, error: 0};

    this.activeSubscriptions.forEach(sub => {
      health.total++;
      switch (sub.status) {
        case 'connected':
          health.connected++;
          break;
        case 'connecting':
          health.connecting++;
          break;
        case 'error':
          health.error++;
          break;
      }
    });

    return health;
  }
}

// Export singleton instance
export const subscriptionManager = SubscriptionManager.getInstance();

// Centralized Token Health Monitor for better token management
class TokenHealthMonitor {
  private static instance: TokenHealthMonitor;
  private refreshInProgress: boolean = false;
  private lastRefreshTime: number = 0;
  private refreshCallbacks: Array<() => void> = [];
  private readonly MIN_REFRESH_INTERVAL = 60000; // 1 minute minimum between refreshes
  private refreshTimeoutId: NodeJS.Timeout | null = null;
  private expiryBuffer = 600; // Increased from 300 to 600 seconds (10 minutes) before expiry to refresh

  private constructor() {
    // Listen for token refresh events
    supabase.auth.onAuthStateChange(event => {
      if (event === 'TOKEN_REFRESHED') {
        this.lastRefreshTime = Date.now();
        this.refreshInProgress = false;

        // Execute any pending callbacks
        this.refreshCallbacks.forEach(callback => callback());
        this.refreshCallbacks = [];
      }
    });
  }

  public static getInstance(): TokenHealthMonitor {
    if (!TokenHealthMonitor.instance) {
      TokenHealthMonitor.instance = new TokenHealthMonitor();
    }
    return TokenHealthMonitor.instance;
  }

  public async ensureValidToken(): Promise<boolean> {
    const sessionStatus = await getSessionStatus();

    if (!sessionStatus.hasSession) {
      return false;
    }

    if (!sessionStatus.expiresAt) {
      return true; // No expiry info, assume valid
    }

    const expiryTime = sessionStatus.expiresAt * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;

    // If token will expire soon, refresh it
    if (timeUntilExpiry < this.expiryBuffer * 1000) {
      return this.refreshTokenIfNeeded();
    }

    return true;
  }

  public async refreshTokenIfNeeded(): Promise<boolean> {
    // Don't refresh if one is already in progress
    if (this.refreshInProgress) {
      return new Promise<boolean>(resolve => {
        this.refreshCallbacks.push(() => resolve(true));
      });
    }

    // Don't refresh if we've refreshed recently
    const timeSinceLastRefresh = Date.now() - this.lastRefreshTime;
    if (timeSinceLastRefresh < this.MIN_REFRESH_INTERVAL) {
      return true;
    }

    this.refreshInProgress = true;

    try {
      const {success} = await refreshSession();
      this.refreshInProgress = !success;
      if (success) {
        this.lastRefreshTime = Date.now();
      }
      return success;
    } catch (err) {
      this.refreshInProgress = false;
      return false;
    }
  }

  // Set up proactive token refresh based on expiry time
  public async setupProactiveRefresh(): Promise<void> {
    // Clear any existing timeout
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }

    const sessionStatus = await getSessionStatus();
    if (!sessionStatus.hasSession || !sessionStatus.expiresAt) {
      return;
    }

    const expiryTime = sessionStatus.expiresAt * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilRefresh =
      expiryTime - currentTime - this.expiryBuffer * 1000;

    // Set timeout to refresh before expiry
    if (timeUntilRefresh > 0) {
      // Set a shorter refresh interval to ensure token is always fresh
      const refreshTime = Math.min(timeUntilRefresh, 30 * 60 * 1000); // Refresh at most every 30 minutes

      this.refreshTimeoutId = setTimeout(() => {
        this.refreshTokenIfNeeded();
        this.setupProactiveRefresh(); // Schedule next refresh after this one completes
      }, refreshTime);
    } else {
      // Token already near expiry, refresh now
      this.refreshTokenIfNeeded().then(() => {
        // Schedule next refresh
        this.setupProactiveRefresh();
      });
    }
  }
}

// Export singleton instance
export const tokenMonitor = TokenHealthMonitor.getInstance();

// Helper function to get current session status
export const getSessionStatus = async () => {
  const {data} = await supabase.auth.getSession();
  return {
    hasSession: !!data.session,
    expiresAt: data.session?.expires_at,
  };
};

// Helper function to manually refresh the session if needed
export const refreshSession = async () => {
  try {
    // First check if we have a session at all
    const {data: sessionData} = await supabase.auth.getSession();
    if (!sessionData.session) {
      return {success: false};
    }

    const {data, error} = await supabase.auth.refreshSession();

    if (error) {
      return {success: false, error: error.message};
    }

    if (data && data.session) {
      return {success: true};
    }

    return {success: false};
  } catch (err) {
    return {success: false};
  }
};

// Helper function to get current authenticated user ID
export const getCurrentUserId = async (): Promise<string | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id || null;
};

// Helper function to verify user is trying to modify their own account
const verifyUserOwnership = async (userId: string): Promise<boolean> => {
  const currentUserId = await getCurrentUserId();
  return currentUserId === userId;
};

type AccountAccessResult = {
  allowed: boolean;
  status: string;
  reactivated: boolean;
  expires_at?: string | null;
  message?: string;
};

export class AccountAccessRestrictedError extends Error {
  readonly status: string;
  readonly expiresAt: string | null;

  constructor(result: AccountAccessResult) {
    const expiryMessage = result.status === 'suspended' && result.expires_at
      ? ` Suspension ends ${new Date(result.expires_at).toLocaleString()}.`
      : '';
    super(`${result.message || 'This account is currently restricted.'}${expiryMessage}`);
    this.name = 'AccountAccessRestrictedError';
    this.status = result.status;
    this.expiresAt = result.expires_at || null;
  }
}

// Function to reactivate a member-paused account. The RPC prevents a client
// from overriding a suspension or ban by writing is_active directly.
export const reactivatePausedAccount = async (userId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user || user.id !== userId) {
    throw new Error('You can only reactivate your own account.');
  }

  const { data, error } = await supabase.rpc('reactivate_paused_account');
  if (error) throw error;
  const result = data as AccountAccessResult;
  if (!result?.allowed) throw new AccountAccessRestrictedError(result);

  return {
    success: true,
    message: result.reactivated ? 'Account reactivated successfully' : 'Account access confirmed',
    ...result,
  };
};

export const pauseUserAccount = async (userId: string, reason?: string) => {
  // Verify user can only pause their own account
  const isOwner = await verifyUserOwnership(userId);
  if (!isOwner) {
    throw new Error('Unauthorized: You can only pause your own account');
  }

  const now = new Date().toISOString();
  const updates = {
    is_active: false,
    updated_at: now,
  };

  try {
    // Update profiles table
    const {error: profileError} = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId);

    if (profileError) {
      throw new Error('Failed to pause profile: ' + profileError.message);
    }

    // Update users table if it exists
    const {error: userError} = await supabase
      .from('users')
      .update({is_active: false})
      .eq('id', userId);

    if (userError) {
      // Don't throw here as users table might not have this record
    }

    // Sign out the user
    await supabase.auth.signOut();

    return {success: true, message: 'Account paused successfully'};
  } catch (error: any) {
    throw new Error(error.message || 'Failed to pause account');
  }
};

export const permanentlyDeleteUserAccount = async (userId: string) => {
  // Verify user can only delete their own account
  const isOwner = await verifyUserOwnership(userId);
  if (!isOwner) {
    throw new Error('Unauthorized: You can only delete your own account');
  }

  try {

    // Get the current session token for API authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No valid session found. Please log in again.');
    }

    // Call our backend API endpoint for secure auth deletion
    const response = await fetch('/api/auth/delete-account', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `API Error: ${response.status}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Backend deletion failed');
    }

    // Sign out the user after successful deletion
    // Note: The user might already be signed out if auth.users was deleted
    try {
      await supabase.auth.signOut();
    } catch (signOutError) {
      // User might already be deleted from auth, ignore sign out error
    }

    return {
      success: true,
      message: data.message || 'Account completely and permanently deleted successfully',
      summary: data,
      method: data.method || 'backend_api',
      authDeleted: data.authDeleted || false,
      authDisabled: data.authDisabled || false,
      userId: data.userId,
      email: data.email
    };
  } catch (error: any) {

    // If the API call fails, fall back to the old direct RPC method
    if (error.message?.includes('fetch') || error.message?.includes('API Error')) {

      try {
        const { data: fallbackData, error: fallbackError } = await supabase
          .rpc('permanently_delete_user_simple', { user_uuid: userId });

        if (fallbackError || !fallbackData?.success) {
          throw new Error('Both API and RPC methods failed');
        }

        await supabase.auth.signOut();

        return {
          success: true,
          message: 'Account deleted via fallback method',
          summary: fallbackData,
          method: 'rpc_fallback',
          authDeleted: false,
          authDisabled: true
        };
      } catch (fallbackError) {
    // Console statement removed
  }
    }

    throw new Error(error.message || 'Failed to permanently delete account');
  }
};

supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === 'SIGNED_OUT' || event === ('USER_DELETED' as any)) {
    // Only clear auth-related items
    const authRelatedKeys = [
      'supabase.auth.token',
      'supabase.auth.refreshToken',
      'authSession',
      'registration_email',
      'registration_password',
      'registration_photo_uris',
      'registration_primary_photo_index',
    ];

    authRelatedKeys.forEach(key => localStorage.removeItem(key));
  }

  // Check for paused account reactivation on sign in (non-blocking)
  if (event === 'SIGNED_IN' && session?.user?.id) {
    // Run reactivation check in the background without blocking auth flow
    (async () => {
      try {

        // Check if the user's account is paused (is_active = false)
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('is_active')
          .eq('id', session.user.id)
          .single();

        if (profileError) {
          return;
        }

        // If account is paused, reactivate it
        if (profile && profile.is_active === false) {
          try {
            await reactivatePausedAccount(session.user.id);
          } catch (reactivationError) {
            if (reactivationError instanceof AccountAccessRestrictedError) {
              setTimeout(() => { supabase.auth.signOut(); }, 0);
            }
          }
        } else {
    // Console statement removed
  }
      } catch (error) {
        // Don't throw here - we don't want to affect the user experience
      }
    })();
  }

  if (event === 'TOKEN_REFRESHED' && session) {
    // Session refreshed silently
  }
});
